import { ErrorHandler, inject, Injectable } from "@angular/core";
import { environment, EventBusService, LogService, UserService } from "@mypxplat/xplat/core";
import { BugsnagErrorHandler } from "@bugsnag/plugin-angular";
import Bugsnag from "@bugsnag/js";
import { Router } from "@angular/router";
import { isChunkLoadError } from "./chunk-load-error.util";

@Injectable()
export class WebErrorHandler extends ErrorHandler {
  log: LogService;
  router: Router;
  eventBusService: EventBusService;
  userService: UserService;
  bugSnag: BugsnagErrorHandler;

  constructor() {
    super();
    this.log = inject(LogService);
    this.router = inject(Router);
    this.eventBusService = inject(EventBusService);
    this.userService = inject(UserService);
    this.bugSnag = new BugsnagErrorHandler();
  }

  handleError(error: any) {
    // Guard against undefined/null errors that can occur from unhandled promise rejections
    // with no value (e.g., Promise.reject() or Promise.reject(undefined))
    if (error === undefined || error === null) {
      this.log.debug("Error handler received undefined/null error - likely an unhandled promise rejection with no value");
      return;
    }

    // Filter out benign browser warnings that don't indicate actual application errors
    const errorMessage = error?.message || error?.toString?.() || "";
    if (errorMessage.includes("ResizeObserver loop completed with undelivered notifications") || errorMessage.includes("ResizeObserver loop limit exceeded")) {
      return;
    }

    // Get the current route and origin
    const currentUrl = this.router.url;
    const currentOrigin = window.location.origin;
    // Check if we should skip Bugsnag reporting
    const isCheckoutRoute = !environment.features.bugsnag_on_checkout && currentUrl.startsWith("/checkout");
    const isAdminApp = environment.adminUrl && currentOrigin.startsWith(environment.adminUrl.replace(/\/$/, ""));

    if (isCheckoutRoute || isAdminApp) {
      // Optionally log the error locally, but don't report to Bugsnag
      this.log.debug(isAdminApp ? "Error in admin app. Not reporting to Bugsnag." : "Error on /checkout route. Not reporting to Bugsnag.");
      console.log(error);
    } else {
      // Proceed with normal error handling (including Bugsnag)
      this.log.debug("error", error);

      if (error && error.status && (error.status == 403 || error.status == 401)) {
        if (error.url && error.url.indexOf("paeapi") > -1) {
          this.userService.tokenExpired$.next(true);
        }
      } else if (error?.error?.error) {
        const errorMsg = error.error.error;
        if (errorMsg == "Your access token is invalid or has expired.") {
          this.userService.tokenExpired$.next(true);
        }
      } else if (isChunkLoadError(error)) {
        this.eventBusService.emit(this.eventBusService.types.showNewCodeAlert, true);
      } else if (error?.status === 0 && (error?.name === "HttpErrorResponse" || errorMessage.includes("0 Unknown Error"))) {
        // Transient client-side network failure. Angular surfaces these as an HttpErrorResponse with
        // status 0 / "Unknown Error" when the request never reached the server: the device is offline,
        // the request was aborted (e.g. navigating away mid-request), or it was blocked by a browser
        // extension/ad-blocker/CORS. These are not actionable application errors, so log locally but
        // don't report them to Bugsnag.
        this.log.debug("Ignoring transient network error (status 0). Not reporting to Bugsnag.", error);
      } else if (error?.code === "auth/network-request-failed" || (error?.name === "FirebaseError" && errorMessage.includes("network-request-failed"))) {
        // Transient Firebase Auth network failure (auth/network-request-failed). The Firebase Auth SDK
        // throws this when one of its own requests times out, is interrupted, or the host is unreachable
        // (e.g. the user is offline or navigates away mid-request during login/logout). The entire stack
        // originates inside the minified Firebase SDK, there is no application code path to correct, and
        // Firebase itself classifies it as a network condition rather than a bug. Like the status-0 guard
        // above, log locally but don't report it to Bugsnag. Actionable Firebase errors with other codes
        // (e.g. permission-denied, auth/invalid-custom-token) are intentionally NOT matched here.
        this.log.debug("Ignoring transient Firebase auth network error (auth/network-request-failed). Not reporting to Bugsnag.", error);
      } else if (error?.name === "FirebaseError" && (error?.code === "unavailable" || errorMessage.includes("client is offline"))) {
        // Transient Firestore backend-unavailable failure (Firestore status code "unavailable", e.g.
        // "Failed to get document because the client is offline."). The Firestore SDK rejects reads with
        // this when it cannot reach the backend — the device dropped its connection, the request was
        // interrupted mid-navigation (this fires most on /logout while subscriptions tear down), or the
        // backend simply did not respond in time. The whole stack originates inside the minified Firestore
        // SDK, there is no application code path to correct, and Firebase itself classifies it as a network
        // condition rather than a bug (it even logs "your device does not have a healthy Internet
        // connection"). Mirroring the auth/network-request-failed guard above, log locally but don't report
        // it to Bugsnag. Actionable Firestore errors with other codes (e.g. permission-denied) are
        // intentionally NOT matched here.
        this.log.debug("Ignoring transient Firestore offline error (unavailable/client is offline). Not reporting to Bugsnag.", error);
      } else {
        // report the error to Bugsnag
        this.log.info("Submitting to Bugsnag.");
        this.bugSnag.handleError(error); // <-- Call the original BugsnagErrorHandler's handleError
      }
    }
  }

  reportError(error) {
    Bugsnag.notify(error);
  }
}
